import { differenceInYears, parseISO } from 'date-fns';
import { Case } from '../types/Case/Case.type';
import { BuilderAge } from '../types/enums/BuilderAge.enum';
import { BuilderCatalogClass } from '../types/enums/BuilderCatalogClass.enum';
import { BuilderCharacteristic } from '../types/enums/BuilderCharacteristic.enum';
import { BuilderStage } from '../types/enums/BuilderStage.enum';
import { BuilderVocabularyItem } from '../types/SimilarlySituated/BuilderVocabularyItem.type';
import { CaseType } from '../types/SimilarlySituated/CaseType.type.ts';
import { SsbCharacteristic } from '../types/SimilarlySituated/SsbCharacteristic.type';
import { SsbCondition } from '../types/SimilarlySituated/SsbCondition.type';
import { Statute } from '../types/Statute.type';
import { normalizeText } from './normalizeText';
import { findCharacteristic } from './ssbCatalogMetadata';

// default values for statute identity facets
export const ANY_FACET_VALUE: BuilderVocabularyItem = { id: -1, name: 'Any' };
const DEFAULT_STAGE: BuilderVocabularyItem = { id: BuilderStage.CHARGING, name: 'Charging' };

const JUVENILE_AGE: BuilderVocabularyItem = { id: BuilderAge.JUVENILE, name: 'Juvenile (<18)' };
const YOUTHFUL_AGE: BuilderVocabularyItem = { id: BuilderAge.YOUTHFUL, name: 'Youthful (<26)' };

export function createEmptyCaseType(): CaseType {
  return { id: crypto.randomUUID(), conditions: [], excepts: [] };
}

function createCharacteristicCondition(characteristicId: BuilderCharacteristic): SsbCondition | null {
  const characteristic = findCharacteristic(characteristicId);
  return characteristic ? createCondition(characteristic) : null;
}

function createStatuteCondition(characteristicId: BuilderCharacteristic, statute: Statute): SsbCondition | null {
  const condition = createCharacteristicCondition(characteristicId);
  return condition && { ...condition, statutes: [statute] };
}

function clientAgeAtOffense(caseData: Case): BuilderVocabularyItem | null {
  if (!caseData.client_birthday || !caseData.offense_date) return null;

  const age = differenceInYears(parseISO(caseData.offense_date), parseISO(caseData.client_birthday));
  if (age < 18) return JUVENILE_AGE;
  if (age < 26) return YOUTHFUL_AGE;
  return null;
}

function caseEnhancements(caseData: Case): Statute[] {
  const enhancements = [
    ...(caseData.statutes ?? []).filter((statute) => statute.is_enhancement),
    ...(caseData.charge_enhancements ?? []).flat().filter((statute) => statute.is_charge_enhancement),
  ];

  // a charge enhancement can also be listed among the case statutes
  return enhancements.filter((statute, index) => enhancements.findIndex((s) => s.id === statute.id) === index);
}

// load first case type for untouched new build based on case details data
export function createCaseTypeFromCase(caseData?: Case): CaseType {
  const caseType = createEmptyCaseType();
  if (!caseData) return caseType;

  const charges = (caseData.statutes ?? []).filter((statute) => !statute.is_enhancement);
  const age = clientAgeAtOffense(caseData);
  const ageCondition = age ? createCharacteristicCondition(BuilderCharacteristic.AGE) : null;

  caseType.conditions = [
    ...charges.map((statute) => createStatuteCondition(BuilderCharacteristic.SPECIFIC_STATUTE, statute)),
    ...caseEnhancements(caseData).map((statute) => createStatuteCondition(BuilderCharacteristic.ENHANCEMENT, statute)),
    ageCondition && { ...ageCondition, value: age ?? undefined },
  ].filter((condition): condition is SsbCondition => condition !== null);

  return caseType;
}

export function createCondition(characteristic: SsbCharacteristic): SsbCondition {
  return {
    id: crypto.randomUUID(),
    characteristicId: characteristic.id,
    statutes: characteristic.conditionClass === BuilderCatalogClass.STATUTE_IDENTITY ? [] : undefined,
    groupings: characteristic.groupingVocabulary ? [] : undefined,
    stages: characteristic.stageMode === 'select' ? [DEFAULT_STAGE] : undefined,
    statuteLevel: characteristic.facets?.statuteLevel ? ANY_FACET_VALUE : undefined,
    position: characteristic.facets?.position ? ANY_FACET_VALUE : undefined,
  };
}

export function isConditionComplete(condition: SsbCondition): boolean {
  const characteristic = findCharacteristic(condition.characteristicId);
  if (!characteristic?.conditionClass) return false;

  if (characteristic.stageMode === 'select' && !condition.stages?.length) return false;
  if (characteristic.groupingVocabulary && !condition.groupings?.length) return false;

  switch (characteristic.conditionClass) {
    case BuilderCatalogClass.STATUTE_IDENTITY:
      return (condition.statutes?.length ?? 0) > 0;
    case BuilderCatalogClass.CATEGORICAL:
      return Boolean(characteristic.groupingVocabulary || condition.value);
    case BuilderCatalogClass.DATE_RANGE:
      return Boolean(condition.value) && Boolean(condition.dateFrom || condition.dateTo);
    case BuilderCatalogClass.BOOLEAN_FLAG:
    default:
      return true;
  }
}

export function sortStages(stages: BuilderVocabularyItem[] = []): BuilderVocabularyItem[] {
  return [...stages].sort((a, b) => b.id - a.id);
}

export function isDispositionAvailable(stages: BuilderVocabularyItem[] = []): boolean {
  return stages.length === 1 && stages[0].id === BuilderStage.CONVICTION;
}

export function conditionCode(condition: SsbCondition): string {
  const [first] = condition.statutes ?? [];
  if (first) return first.display_name;

  return findCharacteristic(condition.characteristicId)?.label ?? String(condition.characteristicId);
}

export function conditionText(condition: SsbCondition): string {
  const characteristic = findCharacteristic(condition.characteristicId);
  if (!characteristic) return '';

  if (characteristic.groupingVocabulary) {
    return (condition.groupings ?? []).map((grouping) => grouping.name).join(', ');
  }

  switch (characteristic.conditionClass) {
    case BuilderCatalogClass.STATUTE_IDENTITY:
      return normalizeText(condition.statutes?.[0]?.description);
    case BuilderCatalogClass.CATEGORICAL:
      return condition.value?.name ?? '';
    case BuilderCatalogClass.DATE_RANGE: {
      const window = [
        condition.dateFrom ? `from ${condition.dateFrom}` : '',
        condition.dateTo ? `to ${condition.dateTo}` : '',
      ].filter(Boolean);
      return [condition.value?.name, ...window].filter(Boolean).join(' ');
    }
    default:
      return '';
  }
}

export function conditionLabel(condition: SsbCondition): string {
  const statutes = condition.statutes ?? [];

  if (statutes.length > 0) {
    const anyOf = statutes
      .map((statute) => [statute.display_name, normalizeText(statute.description)].filter(Boolean).join(' '))
      .join(' OR ');
    const stages = sortStages(condition.stages)
      .map((stage) => stage.name)
      .join(', ');

    return [anyOf, stages ? `@ ${stages}` : ''].filter(Boolean).join(' ');
  }

  const code = conditionCode(condition);
  const text = conditionText(condition);

  return [code, text === code ? '' : text].filter(Boolean).join(' ');
}

export function deriveCaseTypeName(caseType: CaseType): string {
  if (caseType.customName?.trim()) return caseType.customName;
  if (caseType.conditions.length === 0) return 'Untitled case type card';

  return caseType.conditions.map((condition) => conditionText(condition) || conditionCode(condition)).join(' + ');
}
